import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Monitor,
  CheckCircle,
  HelpCircle,
  BookOpen,
  Sparkles,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { fireEvent } from '../utils/funnelEvents';

const CANONICAL = 'https://www.getsortio.com/hazel-for-windows';
const LAST_UPDATED = '2026-06-09';

const TABLE = {
  headers: ['', 'Hazel', 'Sortio', 'File Juggler', 'DropIt'],
  rows: [
    ['Runs on Windows', 'No (macOS only)', 'Yes (and macOS)', 'Yes', 'Yes'],
    ['Approach', 'Rules you write', 'AI prompts plus an AI Rule Builder', 'Rules with regex and conditions', 'Drag-target associations'],
    ['Reads document contents', 'Rule conditions can match text', 'Yes for text-based formats (PDF text layer, Word, Excel, text) with the content toggle on', 'Yes, including OCR for scanned PDFs', 'No (name and extension)'],
    ['OCR for image-only scans', 'No (needs an existing text layer)', 'No (image-only scans sort by filename and metadata)', 'Yes', 'No'],
    ['Watch folders', 'Yes', 'Yes (Pro)', 'Yes', 'Yes'],
    ['Preview and undo', 'Limited revert', 'Preview before apply, one-click undo', 'Limited', 'Limited'],
    ['Price', '$42 one-time', 'Free tier; Pro $14.99/mo or $99/yr', '$30 one-time', 'Free (open source)'],
    ['Best for', 'Mac rule engineers', 'Messy folders, content-dependent sorting, mixed Mac and Windows', 'Windows power users with stable rule patterns', 'Technical users who like configuring associations'],
  ],
};

const FAQ = [
  {
    question: 'Is there a Hazel for Windows?',
    answer:
      'No. Hazel is built by Noodlesoft exclusively for macOS, on top of Mac-specific frameworks like Spotlight metadata and Finder tags, and no Windows version exists or has been announced. Every "Hazel for Windows" search ends at an alternative: the closest options are Sortio (AI plus rules, runs on Windows and macOS), File Juggler (rules plus OCR, Windows only), and DropIt (free, open source, Windows only).',
  },
  {
    question: 'What is the closest Windows equivalent to Hazel?',
    answer:
      'For a like-for-like rule engine, File Juggler: watched folders, conditions, regex, and content matching with OCR, for a $30 one-time license. For the broader job Hazel users actually hire it for (keeping folders organized without manual filing), Sortio covers the rules workflow and adds AI sorting by prompt, which handles the files rules cannot describe.',
  },
  {
    question: 'Is there a free Hazel alternative for Windows?',
    answer:
      'DropIt is free and open source: you drag files onto a floating target and it routes them by the associations you configure. Sortio\'s free tier is also free indefinitely: a one-time AI trial allowance to test AI sort, plus unlimited rule-based sorting, semantic search, and file chat.',
  },
  {
    question: 'Can Sortio do what Hazel does on Windows?',
    answer:
      'The core Hazel workflow, watch a folder, rename files, route them by what they are, yes: Sortio\'s AI Rule Builder generates deterministic rules from a plain-English description, and Pro adds watch folders. What Sortio does not replicate is Hazel\'s deep AppleScript and shell-script hooks, which are Mac-specific anyway.',
  },
  {
    question: 'Does Sortio OCR scanned documents on Windows?',
    answer:
      'No. Sortio reads text-based formats (PDFs with a text layer, Word, Excel, PowerPoint, plain text) when content sorting is enabled, but it does not OCR image-only scans; those sort by filename and metadata. If scanned-paper workflows are your main job, File Juggler\'s built-in OCR is the stronger pick, or run OCRmyPDF first and let Sortio sort the output.',
  },
  {
    question: 'I am switching from Mac to Windows. Can I bring my Hazel rules?',
    answer:
      'Not directly into anything; Hazel rules only run in Hazel on a Mac. The practical migration is to describe each rule in plain English to Sortio\'s AI Rule Builder, which generates the equivalent rule, and then those rules run identically on your Windows machine and any Mac you still use. Our Hazel migration guide covers the common patterns.',
  },
];

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Hazel for Windows: It Does Not Exist. Here Is What to Use Instead.',
  description:
    'Hazel is macOS-only. The honest rundown of what Windows users can use instead: Sortio, File Juggler, and DropIt, compared on rules, content reading, OCR, and price.',
  url: CANONICAL,
  datePublished: LAST_UPDATED,
  dateModified: LAST_UPDATED,
  about: {
    '@type': 'SoftwareApplication',
    name: 'Sortio',
    url: 'https://www.getsortio.com',
  },
  publisher: {
    '@type': 'Organization',
    name: 'CMG Labs LLC',
    url: 'https://www.getsortio.com',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
    { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://www.getsortio.com/compare' },
    { '@type': 'ListItem', position: 3, name: 'Hazel for Windows', item: CANONICAL },
  ],
};

const OPTIONS = [
  {
    name: 'Sortio',
    verdict: 'The closest thing to "Hazel, but it reads the files, and it runs on Windows."',
    body: [
      'Sortio covers the two jobs Hazel users come looking for. The rule side: the AI Rule Builder turns a plain-English description ("PDFs with invoice in the name go to Finance, renamed to date_vendor") into a deterministic rule, and Pro watch folders run it automatically as files arrive. The AI side: for folders where filenames tell you nothing, you type a prompt and Sortio reads each text-based document to decide where it belongs, shows you the full plan, and applies it only when you approve.',
      'It is also the only option here that runs on both Windows and macOS with identical behavior, which matters if you switched platforms (a common reason people search for Hazel on Windows) or run a mixed household. The free tier (a one-time AI trial allowance, unlimited rule-based sorting) is enough to test it on a real folder.',
      'Honest limits: no OCR for image-only scans, AI sort caps at 5,000 files per run, and watch folders require Pro ($14.99/mo or $99/yr).',
    ],
  },
  {
    name: 'File Juggler',
    verdict: 'The like-for-like rule engine on Windows, with working OCR. Windows-only.',
    body: [
      'File Juggler is the closest functional sibling to Hazel on Windows: watched folders, conditions and actions, regex on names and contents, and built-in OCR that lets rules match text inside scanned PDFs, something neither Hazel nor Sortio does natively. It costs $30 one-time and has a loyal long-term user base.',
      'The trade-offs are the same ones Hazel users know: every pattern needs a rule, brittle documents defeat regex, and there is no preview-then-apply loop for a big backlog cleanup. And it is Windows-only, so it does not travel if you ever go back to a Mac.',
    ],
  },
  {
    name: 'DropIt',
    verdict: 'Free and open source. Old-school, capable, hands-on.',
    body: [
      'DropIt gives you a floating drop target: drag files onto it and they get routed by the associations you have configured (extension, name pattern, regex, date). It also supports monitored folders. It is free, open source, and has years of documentation behind it.',
      'The experience is deliberately manual and the interface shows its age. If you enjoy configuring associations and want to spend nothing, DropIt is a fine answer. If you want files read and filed for you, it is not trying to be that.',
    ],
  },
];

const HazelForWindowsPage = () => {
  const downloadClick = (ctaId) => () =>
    fireEvent('cta_click', { cta_id: ctaId, page: '/hazel-for-windows', target: '/download' });

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="Hazel for Windows: What to Use Instead (2026) | Sortio"
        description="There is no Hazel for Windows; Hazel is macOS-only. Honest comparison of the real Windows options: Sortio (AI plus rules), File Juggler (rules plus OCR), and DropIt (free)."
        canonical={CANONICAL}
        schema={[articleSchema, faqSchema, breadcrumbSchema]}
        keywords="hazel for windows, hazel windows alternative, hazel alternative windows, file organizer windows"
        lastModified={LAST_UPDATED}
      />

      <div className="container mx-auto px-6 max-w-4xl py-16 lg:py-24 relative z-10">
        <Breadcrumbs items={[{ label: 'Compare', href: '/compare' }, { label: 'Hazel for Windows', href: null }]} />

        <div className="mb-10">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Monitor className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-semibold mb-6 text-apple-50">
            Hazel for Windows does not exist. Here is what to use instead.
          </h1>
          <p className="text-apple-300 text-lg leading-relaxed">
            Hazel is macOS-only and always has been. If you came from a Mac, heard about it from a
            productivity blog, or just want your Windows Downloads folder to file itself, these are the
            real options, compared honestly.
          </p>
          <p className="text-apple-400 text-sm mt-4">Last updated {new Date(`${LAST_UPDATED}T00:00:00`).toLocaleDateString()}</p>
        </div>

        {/* Why no Hazel on Windows */}
        <section className="mb-14 space-y-4 text-apple-200 leading-relaxed">
          <p>
            Hazel is built by Noodlesoft on top of Mac-specific plumbing: Spotlight metadata, Finder tags,
            AppleScript. That is why it is excellent on macOS and why a Windows port has never shipped and
            is not on any announced roadmap. Windows itself does not fill the gap; File Explorer grouping
            and Storage Sense can tidy by type and age, but neither can rename a file from its contents or
            route an invoice to the right client folder.
          </p>
          <p>
            So the practical question is not "where do I get Hazel for Windows" but "which Windows tool
            covers the job I wanted Hazel for." Three do, in different ways.
          </p>
        </section>

        {/* Comparison table */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6">The options at a glance</h2>
          <div className="overflow-x-auto rounded-2xl border border-apple-600/30 bg-apple-700/20">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-apple-600/30">
                  {TABLE.headers.map((h, i) => (
                    <th key={i} className={`px-4 py-3 text-left font-medium ${i === 0 ? 'text-apple-300' : 'text-apple-50'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TABLE.rows.map((row, r) => (
                  <tr key={r} className="border-b border-apple-600/20 last:border-0">
                    {row.map((cell, c) => (
                      <td key={c} className={`px-4 py-3 align-top ${c === 0 ? 'text-apple-400 font-medium' : 'text-apple-200'}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Per-option breakdown */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6">The breakdown</h2>
          <div className="space-y-8">
            {OPTIONS.map((o) => (
              <div key={o.name} className="bg-apple-700/30 rounded-2xl border border-apple-600/30 p-6">
                <h3 className="text-xl font-semibold text-apple-50 mb-2">{o.name}</h3>
                <p className="text-sortio-primary text-sm mb-4 italic">{o.verdict}</p>
                <div className="space-y-3 text-apple-200 leading-relaxed">
                  {o.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Verdict */}
        <section className="mb-14">
          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-8">
            <h2 className="text-2xl font-semibold text-apple-50 mb-4 flex items-center">
              <CheckCircle className="text-sortio-primary mr-3" size={24} />
              Which one should you pick?
            </h2>
            <div className="space-y-3 text-apple-200 leading-relaxed">
              <p>
                Want the Hazel experience, rules you author, fired by watched folders, and you work with
                scanned paper? File Juggler, $30, done.
              </p>
              <p>
                Want to spend nothing and do not mind configuring everything by hand? DropIt.
              </p>
              <p>
                Want the folder to organize itself from a sentence, with a preview before anything moves,
                on Windows today and on a Mac if you ever switch back? That is Sortio, and the free tier
                lets you confirm it on your own files before paying anything.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
            <HelpCircle className="text-sortio-primary mr-3" size={24} />
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Keep reading */}
        <section className="mb-14">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
            <BookOpen className="text-sortio-primary mr-3" size={24} />
            Keep reading
          </h2>
          <div className="grid gap-3">
            {[
              { label: 'Hazel vs Sortio: the direct head-to-head', href: '/hazel-vs-sortio' },
              { label: 'FileJuggler vs Sortio, compared in depth', href: '/compare/filejuggler-vs-sortio' },
              { label: 'Migrate your Hazel rules to Sortio', href: '/guides/migrate-from-hazel-to-sortio' },
              { label: 'What an AI file organizer actually does', href: '/ai-file-organizer' },
            ].map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
              >
                <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">{l.label}</span>
                <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try Sortio free on Windows</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              Free download, no credit card. A one-time AI trial allowance plus unlimited rule-based sorting,
              enough to point it at your Downloads folder and watch the preview.
            </p>
            <Link
              to="/download"
              onClick={downloadClick('hazel_for_windows_footer')}
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio for Windows
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HazelForWindowsPage;
